/**
 * compressed-tensors `pack-quantized`: 4-bit weights inside plain `U8` bytes.
 *
 * ## The failure this exists to fix
 *
 * `moonshotai/Kimi-K3` stores every expert projection as a pair:
 *
 *     ...experts.0.w1.weight_packed   U8  [3072, 1792]
 *     ...experts.0.w1.weight_scale    U8  [3072, 112]
 *
 * Both are `U8`, so counting elements gives 8 bits per element and the header
 * line reads **1503B params · 8.30 bits/weight**. The model is 2.78T parameters
 * at **4.49 bits/weight**. Every byte of `weight_packed` holds two 4-bit values,
 * and `weight_scale` is not weights at all.
 *
 * That was live on the flagship model's share card, and it disagreed with the
 * model card by nearly a factor of two. Worse, the tool's whole claim is that
 * its numbers come from the file — so being confidently wrong here is the exact
 * failure the project is built to avoid.
 *
 * ## Why it slipped past the MXFP4 folder we already had
 *
 * `block-scaled.ts` folds this same idea for `gpt-oss`'s `_blocks`/`_scales`
 * naming. This is the same scheme under a different container: the repo
 * declares `quant_method: "compressed-tensors"` — a *wrapper* — with the actual
 * format in `format: "mxfp4-pack-quantized"`. Reading only `quant_method` saw a
 * name no scheme test matched. Two layouts, one idea, and the lesson is that
 * the container is not the scheme.
 *
 * ## What makes the pair checkable rather than assumed
 *
 * The scale plane fixes the group count and the packed plane fixes the byte
 * count, and they have to agree:
 *
 *     scale elements x group_size  ==  packed bytes x (8 / bits)
 *
 * Measured across all 96 shards of Kimi-K3, that is
 * `85 085 650 944 x 32 == 1 361 370 415 104 x 2 == 2 722 740 830 208`, exactly,
 * and exactly the Hub's own count of that repo's `U8` parameters. Two
 * independent instruments agreeing on a number neither derived from the other.
 *
 * The second check has teeth: the payload alone must come out at *exactly* the
 * declared width. A pair whose geometry says 4.03 bits is not this layout being
 * slightly off — it is some other layout wearing these suffixes.
 *
 * ## Why the declaration is required
 *
 * Shapes cannot separate 4-bit/group-32 from 8-bit/group-64: one equation
 * constrains only the product, the trap `mlx.ts` documents at length. So bits
 * and group size come from the config and the shapes *verify* them. A repo that
 * declares neither folds nothing and reports its bytes honestly, which is worse
 * information than a correct fold and better information than a guessed one.
 *
 * ## …and why "the declaration" is a set
 *
 * Mixed precision is normal for this format.
 * `kaitchup/Qwen3.6-27B-autoscheme-3.5bit-XS` declares three `config_groups`:
 * 4-bit attention, 3-bit MLP, 4-bit `lm_head`, all at group 128. There is no
 * single declared width, and inventing one would misreport whichever half it
 * did not describe — so `quant-config.ts` hands over **candidates** and the
 * choice is made here, per tensor, against that tensor's own shapes.
 *
 * The choice is a filter, not a preference: every candidate is verified and one
 * is kept only if it is the only survivor. Measured on that repo, all 401
 * packed pairs resolve uniquely — `down_proj` accepts (3, 128) and rejects
 * (4, 128), `lm_head` the reverse. Two survivors would be a genuine ambiguity,
 * because `bits x groupSize` is the only product the shapes constrain, so
 * (2, 256) and (4, 128) fit the same bytes and differ two-fold in parameter
 * count. That case claims nothing and says so.
 *
 * That is the reviewer's rule made literal: **the config corroborates, the
 * construction decides.**
 *
 * Pure: shapes in, arithmetic out, no I/O.
 */

import type { DeclaredQuantization, QuantizationCandidate } from "./mlx.js";

export interface PackedTensorLike {
  readonly name: string;
  readonly dtype: string;
  readonly shape: readonly number[];
  readonly byteLength: number;
}

/** What the surviving candidate says about a pair, once one has survived. */
export interface PackedWeightGeometry {
  readonly bits: number;
  readonly groupSize: number;
  /** Weights in the logical matrix, *before* packing. */
  readonly params: number;
  /** What the scheme actually costs, scales and sidecars included. */
  readonly effectiveBpw: number;
  /** The payload alone — must equal `bits` exactly, or this is not the layout. */
  readonly payloadBpw: number;
}

export interface PackedWeightGroup {
  /** Logical name, e.g. `...experts.0.w1.weight`. */
  readonly prefix: string;
  /** The payload tensor — `<prefix>_packed`. */
  readonly packedName: string;
  /** Every physical tensor in the group, payload first. */
  readonly partNames: readonly string[];
  /** Payload plus scales plus whatever else rides along. */
  readonly bytes: number;
  /**
   * The width the shapes picked out of the declared candidates, or `undefined`
   * when more than one survived.
   *
   * Undefined is not "unfolded": the parts are still grouped, because
   * membership is a naming fact and does not depend on the width. What is
   * withheld is the *parameter count*, which is the number that would be wrong.
   * There is no field here to put a made-up one in, which is the point.
   */
  readonly resolved: PackedWeightGeometry | undefined;
  /** Why nothing resolved, when nothing did. Disclosed, never averaged. */
  readonly disagreement: string | undefined;
}

const PACKED_SUFFIX = "_packed";
const SCALE_SUFFIX = "_scale";

/**
 * Storage width of a payload plane, in bits.
 *
 * `pack-quantized` packs into bytes *or* into 32-bit words, and the two are the
 * same layout at different granularity — so the width is a parameter of the
 * construction rather than a gate on it. It was a gate, `{U8, I8}`, which is
 * why `kaitchup/Qwen3.6-27B-autoscheme-3.5bit-XS` fell straight through the
 * detector and rendered `I32` at a flat 32.00 bits per weight everywhere
 * (reviewer, owner-reported, 2026-08-10).
 */
const PAYLOAD_WIDTHS: ReadonlyMap<string, number> = new Map([
  ["U8", 8],
  ["I8", 8],
  ["I32", 32],
  ["U32", 32],
]);

function product(shape: readonly number[]): number {
  return shape.reduce((a, b) => a * b, 1);
}

/**
 * Does the repo declare a packed layout, with the two numbers shapes cannot
 * supply?
 *
 * `pack-quantized` is compressed-tensors' own name for it. Matched on the
 * scheme string, which carries `quant_method` *and* `format` — see
 * `quant-config.ts` for why reading only the former missed this entirely.
 */
function declaresPackQuantized(declared: DeclaredQuantization | undefined): boolean {
  return (declared?.method ?? "").toLowerCase().includes("pack-quantized");
}

/**
 * Test one candidate against one pair, returning its geometry or nothing.
 *
 * `effectiveBpw` is deliberately absent from the result: it depends on the
 * group's *total* bytes, and the sidecars are not gathered until a candidate
 * has won. Computing it here from the two planes alone would produce a number
 * that is almost right, which is the worst kind.
 */
function verifyPair(
  packed: PackedTensorLike,
  scale: PackedTensorLike,
  bits: number,
  groupSize: number,
): Omit<PackedWeightGeometry, "effectiveBpw"> | undefined {
  const width = PAYLOAD_WIDTHS.get(packed.dtype);
  if (width === undefined) return undefined;
  if (bits <= 0 || bits >= width) return undefined;
  if (groupSize <= 0) return undefined;

  // The payload's bytes must match its shape at its own storage width, or the
  // shapes describe something the byte count contradicts.
  if (packed.byteLength !== product(packed.shape) * (width / 8)) return undefined;

  /*
   * Input features, from the scale plane's own last dimension.
   *
   * **Not `payloadElements x valuesPerWord`**, which is how the byte case was
   * written and which does not survive here: at 3 bits a 32-bit word holds
   * 10 2/3 values, because `pack-quantized` packs **bit-contiguously across
   * word boundaries**. Measured on the repo above — 1632 words x 32 = 52 224
   * bits = 17 408 x 3 exactly — so any `32 / bits` must-be-integral assumption
   * is simply false for this family. Deriving the width from the *bit* budget
   * makes that a non-question.
   */
  const inFeatures = (scale.shape.at(-1) ?? 0) * groupSize;
  if (inFeatures <= 0) return undefined;

  // The declared width has to be what the bit budget actually spends.
  const packedLast = packed.shape.at(-1) ?? 0;
  if ((packedLast * width) / inFeatures !== bits) return undefined;

  const rows = product(packed.shape.slice(0, -1));
  const params = rows * inFeatures;
  if (params <= 0) return undefined;

  // The constraint that fixes everything: one scale per group, groups covering
  // exactly the logical weights. Neither plane can supply this alone.
  if (product(scale.shape) * groupSize !== params) return undefined;

  const payloadBpw = (packed.byteLength * 8) / params;
  // Teeth: the payload alone lands on the declared width exactly, or the
  // geometry was read wrong and the fold is refused. This is also what makes
  // the candidate filter sharp — a width the bytes do not spend is rejected
  // rather than rounded towards.
  if (payloadBpw !== bits) return undefined;

  return { bits, groupSize, params, payloadBpw };
}

/**
 * The `{bits, group}` pairs to try, in the order the config stated them.
 *
 * A scheme-wide declaration is one candidate; a mixed-precision config supplies
 * several and no scheme-wide one. Both are read, and de-duplicated, so neither
 * shape of config is a special case here and a repo that somehow states both
 * gets its scheme-wide pair tried alongside the rest rather than instead of
 * them.
 */
function candidatesFor(
  declared: DeclaredQuantization | undefined,
): QuantizationCandidate[] {
  const out: QuantizationCandidate[] = [];
  const add = (bits: number | undefined, groupSize: number | undefined): void => {
    if (bits === undefined || groupSize === undefined) return;
    if (out.some((c) => c.bits === bits && c.groupSize === groupSize)) return;
    out.push({ bits, groupSize });
  };
  add(declared?.bits, declared?.groupSize);
  for (const c of declared?.candidates ?? []) add(c.bits, c.groupSize);
  return out;
}

function describe(c: QuantizationCandidate): string {
  return `${c.bits}-bit group ${c.groupSize}`;
}

/**
 * Every `weight_packed`/`weight_scale` pair whose declared width the shapes
 * confirm.
 *
 * Empty unless the repo declares a `pack-quantized` format *and* states at
 * least one complete `{bits, group}` pair — without both, the split is
 * unknowable from shapes and an unfolded pair reading 8.00 is at least honest
 * about its bytes.
 *
 * A pair that matches no candidate is not returned at all, which is unchanged:
 * `_packed` is a name anyone may use, and a tensor that merely wears the suffix
 * is not this layout. A pair that matches *several* is returned with
 * `resolved: undefined` — the difference being that there the layout is not in
 * doubt, only its width, so the parts still belong together and saying so is
 * more useful than scattering them.
 */
/**
 * Every tensor that is a `<prefix>_…` sibling, indexed by prefix, in one pass.
 *
 * ## Why this is not a `filter` at the call site
 *
 * It was, for one day, and it made the analysis **quadratic**. The group loop
 * walks every tensor; scanning every tensor again inside it to find that
 * group's odds and ends is `O(n²)`, which is invisible on the repos this family
 * was developed against — `kaitchup/Qwen3.6-27B-autoscheme-3.5bit-XS` has 401
 * packed pairs and finishes instantly — and catastrophic on a wide MoE.
 * `moonshotai/Kimi-K3` is 497,220 tensors of `mxfp4-pack-quantized`, where the
 * same code is ~3.3 hours of CPU. Measured, not estimated: 6k tensors took
 * 1.7 s, 24k took 27.2 s, 60k took 172.7 s — 4x the input for 16x the time.
 *
 * That shipped, and its shape is worth naming because nothing caught it: a
 * correctness fix, fully tested, whose tests all use small fixtures. There is
 * no assertion that fails — the answer stays right and only the clock moves,
 * so it reads as working software everywhere except on the largest models,
 * which are exactly the ones a share card is most worth having.
 *
 * ## The index reproduces `startsWith`, deliberately
 *
 * A tensor is attributed to **every** prefix it could have matched, by trying
 * each of its own `_` positions against the known prefixes — not to a single
 * "best" one. That keeps the behaviour identical to the scan it replaces,
 * including the pathological case where nested prefixes (`a.weight` and
 * `a.weight_scale`) both claim `a.weight_scale_shape`. Cheap because a tensor
 * name holds a handful of underscores, so this is linear in practice.
 */
function siblingsByPrefix(
  tensors: readonly PackedTensorLike[],
): Map<string, PackedTensorLike[]> {
  const prefixes = new Set<string>();
  for (const t of tensors) {
    if (t.name.endsWith(PACKED_SUFFIX)) {
      prefixes.add(t.name.slice(0, -PACKED_SUFFIX.length));
    }
  }

  const index = new Map<string, PackedTensorLike[]>();
  for (const t of tensors) {
    for (let at = t.name.indexOf("_"); at !== -1; at = t.name.indexOf("_", at + 1)) {
      const prefix = t.name.slice(0, at);
      if (!prefixes.has(prefix)) continue;
      const bucket = index.get(prefix);
      if (bucket) bucket.push(t);
      else index.set(prefix, [t]);
    }
  }
  return index;
}

export function findPackedWeightGroups(
  tensors: readonly PackedTensorLike[],
  declared?: DeclaredQuantization,
): PackedWeightGroup[] {
  if (!declaresPackQuantized(declared)) return [];
  const candidates = candidatesFor(declared);
  if (candidates.length === 0) return [];

  const byName = new Map(tensors.map((t) => [t.name, t]));
  const siblings = siblingsByPrefix(tensors);
  const groups: PackedWeightGroup[] = [];
  for (const packed of tensors) {
    if (!packed.name.endsWith(PACKED_SUFFIX)) continue;
    const prefix = packed.name.slice(0, -PACKED_SUFFIX.length);
    const scale = byName.get(`${prefix}${SCALE_SUFFIX}`);
    if (!scale) continue;

    // Every candidate, not the first that fits: a second survivor is the whole
    // finding, and short-circuiting would hide it.
    const survivors = candidates
      .map((c) => ({
        candidate: c,
        geometry: verifyPair(packed, scale, c.bits, c.groupSize),
      }))
      .filter((r) => r.geometry !== undefined);
    if (survivors.length === 0) continue;

    /*
     * Every remaining sibling of the same `weight`, by path.
     *
     * `weight_shape` (`I64[2]`) rides along in this family. Sixteen bytes, so
     * it moves no displayed digit — but left out it is not merely uncounted, it
     * is a *stray logical tensor*: two elements in sixteen bytes renders as its
     * own cell at 64 bits per weight. The same module-path rule that catches
     * this family's existence catches its odds and ends.
     */
    const partNames = [packed.name, scale.name];
    const taken = new Set(partNames);
    let bytes = packed.byteLength + scale.byteLength;
    for (const extra of siblings.get(prefix) ?? []) {
      if (taken.has(extra.name)) continue;
      taken.add(extra.name);
      partNames.push(extra.name);
      bytes += extra.byteLength;
    }

    const only =
      survivors.length === 1
        ? (survivors[0]?.geometry as Omit<PackedWeightGeometry, "effectiveBpw">)
        : undefined;
    groups.push({
      prefix,
      packedName: packed.name,
      partNames,
      bytes,
      resolved: only ? { ...only, effectiveBpw: (bytes * 8) / only.params } : undefined,
      disagreement: only
        ? undefined
        : // The shapes constrain `bits x groupSize`, not either alone, so more
          // than one declared pair can fit the same bytes while implying
          // different parameter counts. Naming both is the answer; picking one
          // would be a coin toss reported as a measurement.
          `config declares ${candidates.map(describe).join(" and ")}, and these shapes admit ${survivors
            .map((s) => describe(s.candidate))
            .join(
              " and ",
            )} — which imply different parameter counts, so none is claimed`,
    });
  }
  return groups;
}
